<template>
  <div class="marketing-shop">
    <div class="shop-header">
      <div class="d-flex align-center header-content">
        <div>
          <div class="shop-intro text-h6 font-weight-medium grey--text text--lighten-1">Bestelle Marketing-Material für dein Studio</div>
        </div>
        <v-spacer></v-spacer>

        <v-btn v-if="activeTab === 'shop'" icon large class="cart-btn" @click="openCart">
          <v-badge :content="cartItemCount" :value="cartItemCount > 0" color="error" overlap>
            <v-icon size="28" color="grey lighten-1">mdi-cart</v-icon>
          </v-badge>
        </v-btn>
      </div>

      <v-tabs v-model="activeTab" background-color="transparent" dark color="primary" slider-size="3" class="shop-tabs" show-arrows>
        <v-tab href="#shop" class="text-none">
          <v-icon left small>mdi-storefront</v-icon>
          Shop
        </v-tab>
        <v-tab href="#myOrders" class="text-none">
          <v-icon left small>mdi-package-variant-closed</v-icon>
          Meine Bestellungen
        </v-tab>
        <v-tab href="#inventory" class="text-none">
          <v-icon left small>mdi-clipboard-text-multiple</v-icon>
          Inventar
        </v-tab>
        <v-tab v-if="canManage" href="#allOrders" class="text-none">
          <v-icon left small>mdi-clipboard-list</v-icon>
          Alle Bestellungen
        </v-tab>
        <v-tab v-if="canManage" href="#productAdmin" class="text-none">
          <v-icon left small>mdi-cog</v-icon>
          Produkte verwalten
        </v-tab>
      </v-tabs>
    </div>

    <div class="shop-content">
      <v-tabs-items v-model="activeTab" class="transparent">
        <!-- ============ SHOP ============ -->
        <v-tab-item value="shop">
          <div style="max-width: 1400px; margin: 0 auto; padding: 24px 16px">
            <!-- Filter-Zeile: Suche + Typ -->
            <div class="d-flex flex-wrap align-center mb-6" style="gap: 12px">
              <v-text-field
                v-model="productSearch"
                prepend-inner-icon="mdi-magnify"
                placeholder="Produkt suchen..."
                outlined
                dense
                dark
                hide-details
                clearable
                style="max-width: 500px; flex: 1 1 280px" />

              <v-btn-toggle v-model="productTypeFilter" mandatory dense color="primary" dark>
                <v-btn small value="all">
                  <v-icon left small>mdi-view-grid</v-icon>
                  Alle
                </v-btn>
                <v-btn small value="physical">
                  <v-icon left small>mdi-package-variant</v-icon>
                  Physisch
                </v-btn>
                <v-btn small value="digital">
                  <v-icon left small>mdi-download</v-icon>
                  Downloads
                </v-btn>
              </v-btn-toggle>
            </div>

            <div v-if="loadingProducts" class="text-center pa-12">
              <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
            </div>

            <div v-else-if="filteredProducts.length === 0" class="text-center pa-12 grey--text">
              <v-icon size="64" color="grey darken-1">mdi-package-variant</v-icon>
              <div class="mt-3 subtitle-1">Keine Produkte gefunden</div>
            </div>

            <div v-else class="product-grid">
              <product-card
                v-for="product in filteredProducts"
                :key="product._id"
                :product="product"
                :cart-quantity="getCartQuantity(product._id)"
                @add-to-cart="addToCart"
                @open-detail="openProductDetail" />
            </div>
          </div>
        </v-tab-item>

        <v-tab-item value="myOrders">
          <div style="max-width: 1400px; margin: 0 auto; padding: 24px 16px">
            <my-orders-list :orders="myOrders" :loading="loadingMyOrders" @open-detail="openOrderDetail" />
          </div>
        </v-tab-item>

        <v-tab-item value="inventory">
          <div style="max-width: 1400px; margin: 0 auto; padding: 24px 16px">
            <!-- Filter-Zeile -->
            <v-card dark class="mb-4 pa-3" color="rgba(255,255,255,0.05)" flat>
              <v-row dense>
                <v-col cols="12" sm="6" md="4" v-if="canManage">
                  <v-select
                    v-model="inventoryStudio"
                    :items="studioOptions"
                    item-text="name"
                    item-value="_id"
                    label="Studio wählen"
                    outlined
                    dense
                    dark
                    hide-details />
                </v-col>
                <v-col cols="12" sm="6" md="4" v-else class="d-flex align-center">
                  <div class="subtitle-1 font-weight-medium grey--text">
                    Inventar für: <span class="white--text font-weight-bold ml-1">{{ getSelectedStudioName() }}</span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="inventorySearch"
                    prepend-inner-icon="mdi-magnify"
                    placeholder="Inventar durchsuchen..."
                    outlined
                    dense
                    dark
                    hide-details
                    clearable />
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="auto" class="d-flex align-center">
                  <v-btn icon @click="loadInventory" :loading="loadingInventory">
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>

            <!-- Inventar-Liste -->
            <div v-if="loadingInventory" class="text-center pa-12">
              <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
            </div>

            <div v-else-if="filteredInventory.length === 0" class="text-center pa-12 grey--text">
              <v-icon size="64" color="grey darken-1">mdi-clipboard-text-play-outline</v-icon>
              <div class="mt-3 subtitle-1">Dieses Studio hat noch keine physischen Werbemittel bestellt.</div>
            </div>

            <v-card v-else dark color="rgba(255,255,255,0.03)" flat>
              <v-data-table
                dark
                :headers="inventoryHeaders"
                :items="filteredInventory"
                :items-per-page="50"
                class="transparent"
                no-data-text="Keine Einträge">
                <template v-slot:item.image="{ item }">
                  <v-avatar size="40" tile rounded class="my-2">
                    <inventory-product-thumb :product="item.product" :selected-options="item.selectedOptions" />
                  </v-avatar>
                </template>
                <template v-slot:item.title="{ item }">
                  <span class="font-weight-medium">{{ item.product.title }}</span>
                </template>
                <template v-slot:item.variant="{ item }">
                  <template v-if="item.selectedOptions && item.selectedOptions.length > 0">
                    <v-chip x-small outlined color="grey lighten-2" class="mr-1" v-for="opt in item.selectedOptions" :key="opt.name">
                      {{ opt.name }}: {{ opt.value }}
                    </v-chip>
                  </template>
                  <span v-else class="grey--text text-caption font-italic">Keine Varianten</span>
                </template>
                <template v-slot:item.quantity="{ item }">
                  <v-chip color="success" dark small class="font-weight-bold" style="min-width: 50px; text-align: center">
                    {{ item.totalQuantity }} Stk
                  </v-chip>
                </template>
              </v-data-table>
            </v-card>
          </div>
        </v-tab-item>

        <v-tab-item v-if="canManage" value="allOrders">
          <div style="max-width: 1400px; margin: 0 auto; padding: 24px 16px">
            <admin-orders-list
              :orders="allOrders"
              :loading="loadingAllOrders"
              :studio-options="studioFilterOptions"
              :filter-studio.sync="filterStudio"
              :filter-status.sync="filterStatus"
              @reload="loadAllOrders"
              @open-detail="openOrderDetail" />
          </div>
        </v-tab-item>

        <v-tab-item v-if="canManage" value="productAdmin">
          <div style="max-width: 1400px; margin: 0 auto; padding: 24px 16px">
            <product-admin :studio-options="studioOptions" :products="adminProducts" :loading="loadingAdminProducts" @reload="reloadAllProducts" />
          </div>
        </v-tab-item>
      </v-tabs-items>
    </div>

    <cart-drawer
      v-model="cartOpen"
      :items="cart"
      :products="products"
      :studio-options="studioOptions"
      :default-studio-id="getSelectedStudioId"
      :submitting="submittingOrder"
      @update-quantity="updateCartQuantity"
      @remove-item="removeFromCart"
      @clear-cart="clearCart"
      @submit-order="submitOrder" />

    <product-detail-dialog
      v-model="productDetailOpen"
      :product="currentProduct"
      :cart-quantity="currentProduct ? getCartQuantity(currentProduct._id) : 0"
      @add-to-cart="addToCart" />

    <order-detail-dialog
      v-model="orderDetailOpen"
      :order="currentOrder"
      :can-manage="canManage"
      :available-products="products"
      @reload="reloadCurrentOrder"
      @order-updated="handleOrderUpdated" />
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import { mapState, mapGetters } from "vuex"
import socketService from "@/socketService"

import ProductCard from "./components/ProductCard.vue"
import CartDrawer from "./components/CartDrawer.vue"
import ProductDetailDialog from "./components/ProductDetailDialog.vue"
import MyOrdersList from "./components/MyOrdersList.vue"
import AdminOrdersList from "./components/AdminOrdersList.vue"
import OrderDetailDialog from "./components/OrderDetailDialog.vue"
import ProductAdmin from "./components/ProductAdmin.vue"

const InventoryProductThumb = {
  name: "InventoryProductThumb",
  props: {
    product: { type: Object, required: true },
    selectedOptions: { type: Array, default: () => [] },
  },
  data() {
    return { url: null }
  },
  async mounted() {
    let targetFileId = null

    // Find if any choice matches a choice-specific image
    if (this.selectedOptions && this.selectedOptions.length > 0 && this.product.options) {
      for (const selOpt of this.selectedOptions) {
        const productOpt = this.product.options.find((o) => o.name === selOpt.name)
        if (productOpt) {
          const choice = (productOpt.choices || []).find((c) => c.value === selOpt.value)
          if (choice && choice.imageFileId) {
            targetFileId = choice.imageFileId
            break
          }
        }
      }
    }

    // Fallback to first product image
    if (!targetFileId && this.product.images && this.product.images.length > 0) {
      targetFileId = this.product.images[0].fileId
    }

    if (!targetFileId) return
    try {
      this.url = await api.marketingShop.loadProductImage(this.product._id, targetFileId)
    } catch (e) {
      /* ignore */
    }
  },
  beforeDestroy() {
    if (this.url) URL.revokeObjectURL(this.url)
  },
  template: `
    <div style="width: 100%; height: 100%;">
      <img v-if="url" :src="url" style="width: 100%; height: 100%; object-fit: cover;" />
      <div v-else style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.05);">
        <v-icon small color="grey darken-1">mdi-image-off</v-icon>
      </div>
    </div>`,
}

const CART_STORAGE_KEY = "marketingShop_cart_v1"

export default {
  name: "MarketingShop",
  components: {
    ProductCard,
    CartDrawer,
    ProductDetailDialog,
    MyOrdersList,
    AdminOrdersList,
    OrderDetailDialog,
    ProductAdmin,
    InventoryProductThumb,
  },
  data() {
    return {
      userService,
      activeTab: "shop",

      products: [],
      loadingProducts: false,
      productSearch: "",
      productTypeFilter: "all",

      adminProducts: [],
      loadingAdminProducts: false,

      myOrders: [],
      loadingMyOrders: false,

      allOrders: [],
      loadingAllOrders: false,
      filterStudio: "all",
      filterStatus: "all",

      inventoryStudio: this.getSelectedStudioId || null,
      inventorySearch: "",
      loadingInventory: false,
      inventory: [],
      inventoryHeaders: [
        { text: "Bild", value: "image", sortable: false, width: "60px" },
        { text: "Produkt", value: "title" },
        { text: "Variante", value: "variant", sortable: false },
        { text: "Gesamt bestellt", value: "quantity", align: "center", width: "160px" },
      ],

      cart: [],
      cartOpen: false,
      submittingOrder: false,

      currentProduct: null,
      productDetailOpen: false,

      currentOrder: null,
      orderDetailOpen: false,
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    ...mapGetters("studioStore", ["getSelectedStudioId"]),
    canManage() {
      return userService.hasRight("d_manageMarketingShop")
    },
    canFetch() {
      return userService.hasRight("d_fetchMarketingShop") || userService.hasRight("d_manageMarketingShop")
    },
    cartItemCount() {
      return this.cart.reduce((sum, i) => sum + i.quantity, 0)
    },
    studioOptions() {
      return this.storeStudios.filter((s) => s.code && s.code !== "ot" && s.code !== "alibey").map((s) => ({ _id: s._id, name: s.name }))
    },
    studioFilterOptions() {
      return [{ _id: "all", name: "Alle Studios" }, ...this.studioOptions]
    },
    filteredProducts() {
      let list = this.products
      if (this.productTypeFilter && this.productTypeFilter !== "all") {
        list = list.filter((p) => (p.productType || "physical") === this.productTypeFilter)
      }
      if (this.productSearch && this.productSearch.trim()) {
        const q = this.productSearch.trim().toLowerCase()
        list = list.filter((p) => (p.title || "").toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q))
      }
      return list
    },
    filteredInventory() {
      let list = this.inventory || []
      if (this.inventorySearch && this.inventorySearch.trim()) {
        const q = this.inventorySearch.trim().toLowerCase()
        list = list.filter(
          (i) =>
            (i.product.title || "").toLowerCase().includes(q) ||
            (i.selectedOptions || []).some((o) => (o.value || "").toLowerCase().includes(q)),
        )
      }
      return list
    },
  },
  watch: {
    activeTab(val) {
      if (val === "myOrders") this.loadMyOrders()
      else if (val === "allOrders") this.loadAllOrders()
      else if (val === "productAdmin") this.loadAdminProducts()
      else if (val === "inventory") this.loadInventory()
    },
    filterStudio() {
      this.loadAllOrders()
    },
    filterStatus() {
      this.loadAllOrders()
    },
    inventoryStudio() {
      this.loadInventory()
    },
    cart: {
      deep: true,
      handler() {
        this.persistCart()
      },
    },
  },
  methods: {
    // ===== Cart =====
    loadCartFromStorage() {
      try {
        const raw = localStorage.getItem(CART_STORAGE_KEY)
        if (!raw) return
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) this.cart = parsed
      } catch (e) {
        console.error("Cart-Storage konnte nicht gelesen werden:", e)
      }
    },
    persistCart() {
      try {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(this.cart))
      } catch (e) {
        console.error("Cart-Persist fehlgeschlagen:", e)
      }
    },
    getCartItemKey(item) {
      if (!item.selectedOptions || Object.keys(item.selectedOptions).length === 0) return item.product
      const optStr = Object.entries(item.selectedOptions).sort().map(([k, v]) => `${k}:${v}`).join(",")
      return `${item.product}_${optStr}`
    },
    isSameOptions(opt1, opt2) {
      const o1 = opt1 || {}
      const o2 = opt2 || {}
      const k1 = Object.keys(o1)
      const k2 = Object.keys(o2)
      if (k1.length !== k2.length) return false
      return k1.every((key) => o1[key] === o2[key])
    },
    getCartQuantity(productId) {
      return this.cart
        .filter((i) => i.product === productId)
        .reduce((sum, i) => sum + (i.quantity || 0), 0)
    },
    addToCart({ product, quantity, selectedOptions }) {
      const isDigital = product.productType === "digital"
      const opts = selectedOptions || {}
      const existing = this.cart.find((i) => i.product === product._id && this.isSameOptions(i.selectedOptions, opts))

      if (isDigital) {
        if (existing) {
          existing.quantity = 1
        } else {
          this.cart.push({
            product: product._id,
            title: product.title,
            quantity: 1,
            minQuantity: 1,
            maxQuantity: 1,
            productType: "digital",
            selectedOptions: opts,
          })
        }
      } else {
        const newQty = (existing ? existing.quantity : 0) + quantity
        const max = product.maxQuantity || 100
        const final = Math.min(newQty, max)

        if (existing) {
          existing.quantity = final
        } else {
          this.cart.push({
            product: product._id,
            title: product.title,
            quantity: Math.max(final, product.minQuantity || 1),
            minQuantity: product.minQuantity || 1,
            maxQuantity: product.maxQuantity || 100,
            productType: "physical",
            selectedOptions: opts,
          })
        }
      }

      this.$toast && this.$toast.success(`${product.title} zum Warenkorb hinzugefügt`)
    },
    updateCartQuantity({ itemKey, quantity }) {
      const item = this.cart.find((i) => this.getCartItemKey(i) === itemKey)
      if (!item) return
      if (item.productType === "digital") {
        item.quantity = 1
        return
      }
      const min = item.minQuantity || 1
      const max = item.maxQuantity || 100
      item.quantity = Math.max(min, Math.min(max, quantity))
    },
    removeFromCart(itemKey) {
      this.cart = this.cart.filter((i) => this.getCartItemKey(i) !== itemKey)
    },
    clearCart() {
      this.cart = []
    },
    openCart() {
      this.cartOpen = true
    },

    // ===== Order Submit =====
    async submitOrder({ studio, note }) {
      if (this.cart.length === 0) return

      this.submittingOrder = true
      try {
        const payload = {
          studio,
          note: note || "",
          items: this.cart.map((i) => ({
            product: i.product,
            quantity: i.quantity,
            selectedOptions: i.selectedOptions ? Object.entries(i.selectedOptions).map(([name, value]) => ({ name, value })) : [],
          })),
        }

        const order = await api.marketingShop.createOrder(payload)

        // Falls digitale Items dabei waren: direkt downloaden
        const digitalItems = (order.items || []).filter((i) => i.productType === "digital")
        for (let idx = 0; idx < (order.items || []).length; idx++) {
          const it = order.items[idx]
          if (it.productType === "digital" && it.file?.originalName) {
            try {
              await api.marketingShop.downloadOrderItemFile(order._id, idx, it.file.originalName)
            } catch (e) {
              console.error("Auto-Download fehlgeschlagen:", e)
            }
          }
        }

        this.clearCart()
        this.cartOpen = false

        if (digitalItems.length > 0) {
          this.$toast && this.$toast.success("Bestellung abgeschickt – Downloads gestartet!")
        } else {
          this.$toast && this.$toast.success("Bestellung erfolgreich abgeschickt!")
        }
        this.activeTab = "myOrders"
      } catch (e) {
        console.error("Bestellung fehlgeschlagen:", e)
        const msg = e.response?.data?.error || "Fehler beim Absenden der Bestellung"
        this.$toast ? this.$toast.error(msg) : alert(msg)
      } finally {
        this.submittingOrder = false
      }
    },

    async reloadAllProducts() {
      this.loadAdminProducts()
      this.loadProducts()
    },

    async loadProducts() {
      this.loadingProducts = true
      try {
        this.products = await api.marketingShop.fetchProducts()
      } catch (e) {
        console.error("Fehler beim Laden der Produkte:", e)
      } finally {
        this.loadingProducts = false
      }
    },
    async loadAdminProducts() {
      if (!this.canManage) return
      this.loadingAdminProducts = true
      try {
        this.adminProducts = await api.marketingShop.fetchProducts({ all: true })
      } catch (e) {
        console.error("Fehler beim Laden:", e)
      } finally {
        this.loadingAdminProducts = false
      }
    },
    async loadMyOrders() {
      this.loadingMyOrders = true
      try {
        const params = this.canManage ? { mine: true } : {}
        this.myOrders = await api.marketingShop.fetchOrders(params)
      } catch (e) {
        console.error("Fehler beim Laden:", e)
      } finally {
        this.loadingMyOrders = false
      }
    },
    async loadAllOrders() {
      if (!this.canManage) return
      this.loadingAllOrders = true
      try {
        const params = {}
        if (this.filterStudio && this.filterStudio !== "all") params.studio = this.filterStudio
        if (this.filterStatus && this.filterStatus !== "all") params.status = this.filterStatus
        this.allOrders = await api.marketingShop.fetchOrders(params)
      } catch (e) {
        console.error("Fehler beim Laden:", e)
      } finally {
        this.loadingAllOrders = false
      }
    },
    async loadInventory() {
      const studioId = this.inventoryStudio || this.getSelectedStudioId
      if (!studioId) return
      this.loadingInventory = true
      try {
        this.inventory = await api.marketingShop.fetchInventory({ studio: studioId })
      } catch (e) {
        console.error("Fehler beim Laden des Inventars:", e)
      } finally {
        this.loadingInventory = false
      }
    },
    getSelectedStudioName() {
      const studio = this.storeStudios.find((s) => s._id === this.getSelectedStudioId)
      return studio ? studio.name : "-"
    },

    openProductDetail(product) {
      this.currentProduct = product
      this.productDetailOpen = true
    },
    async openOrderDetail(order) {
      try {
        this.currentOrder = await api.marketingShop.fetchOrder(order._id)
        this.orderDetailOpen = true
      } catch (e) {
        console.error("Order konnte nicht geladen werden:", e)
      }
    },
    async reloadCurrentOrder() {
      if (!this.currentOrder) return
      try {
        this.currentOrder = await api.marketingShop.fetchOrder(this.currentOrder._id)
      } catch (e) {
        console.error(e)
      }
    },
    handleOrderUpdated() {
      if (this.activeTab === "myOrders") this.loadMyOrders()
      if (this.activeTab === "allOrders") this.loadAllOrders()
    },

    setupSocketListeners() {
      socketService.on("marketingOrder:new", this.handleNewOrder)
      socketService.on("marketingOrder:updated", this.handleOrderStatusUpdate)
      socketService.on("marketingOrder:commented", this.handleCommentUpdate)
    },

    teardownSocketListeners() {
      socketService.off("marketingOrder:new", this.handleNewOrder)
      socketService.off("marketingOrder:updated", this.handleOrderStatusUpdate)
      socketService.off("marketingOrder:commented", this.handleCommentUpdate)
    },

    handleNewOrder() {
      if (this.activeTab === "allOrders") this.loadAllOrders()
      this.$toast?.info("Eine neue Bestellung ist eingegangen!")
    },

    handleOrderStatusUpdate(payload) {
      if (this.activeTab === "myOrders") this.loadMyOrders()
      if (this.activeTab === "allOrders") this.loadAllOrders()
      if (this.currentOrder && this.currentOrder._id === payload.orderId) {
        this.reloadCurrentOrder()
      }
    },

    handleCommentUpdate(payload) {
      if (this.currentOrder && this.currentOrder._id === payload.orderId) {
        this.reloadCurrentOrder()
      }
    },
  },
  created() {
    this.loadCartFromStorage()
    this.loadProducts()
  },
  mounted() {
    this.setupSocketListeners()
  },
  beforeDestroy() {
    this.teardownSocketListeners()
  },
}
</script>

<style scoped>
.marketing-shop {
  min-height: 100vh;
  color: #fff;
}

.shop-header {
  background: transparent;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  margin-bottom: 24px;
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px 16px 8px 16px;
}

.shop-intro {
  letter-spacing: 0.5px !important;
}

.shop-tabs {
  max-width: 1400px;
  margin: 0 auto;
}

.cart-btn {
  transition: transform 0.2s ease, color 0.2s ease;
}
.cart-btn:hover {
  transform: scale(1.1);
}
.cart-btn:hover .v-icon {
  color: #fff !important;
}

.shop-content {
  padding-bottom: 80px;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

@media (max-width: 600px) {
  .product-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 12px;
  }
}

.transparent {
  background: transparent !important;
}
</style>
